import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReceivedReviewCard = ({ review, userRole, onResponseSubmit }) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleResponseSubmit = async () => {
    if (!response?.trim()) return;

    setIsSubmitting(true);
    await onResponseSubmit(review?.id, response);
    setIsSubmitting(false);
    setShowResponseForm(false);
    setResponse('');
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            strokeWidth={2}
            className={star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
          />
        ))}
      </div>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-success';
    if (rating >= 3.5) return 'text-warning';
    return 'text-error';
  };

  const responseTemplates = [
    'Thank you for your feedback! We appreciate your business.',
    'We\'re glad you had a great experience!',
    'Thank you for choosing us. We look forward to serving you again!',
    'We appreciate your feedback and will work to improve.'
  ];

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={24} color="white" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-body font-semibold text-foreground">{review?.reviewerName}</h3>
            <p className="font-caption text-sm text-muted-foreground">
              {review?.reviewerType === 'owner' ? 'Trailer Owner' : 'Renter'}
            </p>
            <p className="font-caption text-xs text-muted-foreground mt-1">
              {formatDate(review?.reviewDate)}
            </p>
          </div>
        </div>

        <div className="text-right">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`font-heading text-2xl font-bold ${getRatingColor(review?.rating)}`}>
              {review?.rating?.toFixed(1)}
            </span>
            {renderStars(review?.rating)}
          </div>
        </div>
      </div>

      {/* Booking Details */}
      <div className="bg-muted rounded-lg p-3 mb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Package" size={14} className="text-primary" strokeWidth={2} />
            <span className="font-body text-foreground">{review?.trailerName}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={14} className="text-muted-foreground" strokeWidth={2} />
            <span className="font-caption text-muted-foreground">{review?.rentalDates}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Hash" size={14} className="text-muted-foreground" strokeWidth={2} />
            <span className="font-caption text-muted-foreground">{review?.bookingId}</span>
          </div>
        </div>
      </div>

      {/* Category Ratings */}
      {review?.categories && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Communication</p>
            <div className="flex items-center justify-center">
              {renderStars(review?.categories?.communication)}
            </div>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Condition</p>
            <div className="flex items-center justify-center">
              {renderStars(review?.categories?.condition)}
            </div>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Cleanliness</p>
            <div className="flex items-center justify-center">
              {renderStars(review?.categories?.cleanliness)}
            </div>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Value</p>
            <div className="flex items-center justify-center">
              {renderStars(review?.categories?.value)}
            </div>
          </div>
        </div>
      )}

      {/* Review Comment */}
      {review?.comment && (
        <div className="mb-4">
          <p className="font-body text-foreground leading-relaxed">{review?.comment}</p>
        </div>
      )}

      {/* Photos */}
      {review?.photos?.length > 0 && (
        <div className="flex items-center space-x-2 mb-4">
          {review?.photos?.map((photo, index) => (
            <div key={index} className="w-20 h-20 rounded-lg overflow-hidden">
              <img src={photo} alt={`Review photo ${index + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {/* Helpful Count */}
      <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-border">
        <button className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-smooth">
          <Icon name="ThumbsUp" size={16} strokeWidth={2} />
          <span className="font-caption text-sm">Helpful ({review?.helpful})</span>
        </button>
        <button className="flex items-center space-x-2 text-muted-foreground hover:text-error transition-smooth">
          <Icon name="Flag" size={16} strokeWidth={2} />
          <span className="font-caption text-sm">Report</span>
        </button>
      </div>

      {/* Response Section */}
      {review?.hasResponse ? (
        <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4">
          <div className="flex items-start space-x-3 mb-2">
            <Icon name="MessageSquare" size={16} className="text-primary mt-1" strokeWidth={2} />
            <div className="flex-1">
              <p className="font-body font-medium text-foreground mb-1">Your Response</p>
              <p className="font-body text-sm text-foreground mb-2">{review?.response}</p>
              <p className="font-caption text-xs text-muted-foreground">
                {formatDate(review?.responseDate)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {!showResponseForm ? (
            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              onClick={() => setShowResponseForm(true)}
            >
              Respond to Review
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Your Response
                </label>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e?.target?.value)}
                  placeholder="Thank the reviewer or address their feedback..."
                  rows={3}
                  maxLength={300}
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="font-caption text-xs text-muted-foreground mt-1">
                  {response?.length}/300 characters
                </p>
              </div>

              {/* Quick Response Templates */}
              <div>
                <p className="font-caption text-xs text-muted-foreground mb-2">Quick responses:</p>
                <div className="flex flex-wrap gap-2">
                  {responseTemplates?.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => setResponse(template)}
                      className="px-3 py-1 bg-muted hover:bg-muted/80 rounded-full text-xs font-body text-foreground transition-smooth"
                    >
                      {template}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleResponseSubmit}
                  loading={isSubmitting}
                  disabled={!response?.trim() || isSubmitting}
                  iconName="Send"
                >
                  Submit Response
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowResponseForm(false);
                    setResponse('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReceivedReviewCard;